import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiPlus, FiExternalLink } from '../../utils/icons';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { useT } from '../../i18n';

interface IEmpresa {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  tipo: string;
  status?: string;
}

const CompanyListPage: React.FC = () => {
  const [companies, setCompanies] = useState<IEmpresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();
  const t = useT();

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const response = await api.getCompanies(empresaId || undefined);
        if (response.success && response.data) {
          setCompanies(response.data as IEmpresa[]);
        } else {
          setError(t('companyList.fetchError'));
        }
      } catch {
        setError(t('companyList.connectionError'));
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" /> <span>{t('companyList.loading')}</span>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-0">{t('companyList.title')}</h2>
          <p className="text-muted mb-0">{t('companyList.subtitle')}</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/app/empresas')}>
          <FiPlus /> {t('companyList.newCompany')}
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Table responsive hover bordered>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>{t('companyList.colRazaoSocial')}</th>
            <th>{t('companyList.colNomeFantasia')}</th>
            <th>{t('companyList.colCnpj')}</th>
            <th>{t('companyList.colTipo')}</th>
            <th style={{ width: 90 }}>{t('companyList.colStatus')}</th>
            <th className="text-end" style={{ width: 220 }}>{t('companyList.colAcoes')}</th>
          </tr>
        </thead>
        <tbody>
          {companies.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                {t('companyList.empty')}
              </td>
            </tr>
          ) : (
            companies.map(c => (
              <tr key={c.id} className={c.status === 'Inativo' ? 'text-muted' : ''}>
                <td>{c.id}</td>
                <td><strong>{c.razao_social}</strong></td>
                <td>{c.nome_fantasia || '—'}</td>
                <td>{c.cnpj}</td>
                <td>{t(`tipoEmpresa.${c.tipo}`)}</td>
                <td>
                  <Badge bg={c.status === 'Inativo' ? 'secondary' : 'success'}>
                    {c.status === 'Inativo' ? t('companyList.statusInativo') : t('companyList.statusAtivo')}
                  </Badge>
                </td>
                <td className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/app/empresas/editar/${c.id}`)}
                  >
                    <FiExternalLink /> {t('companyList.open')}
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Container>
  );
};

export default CompanyListPage;
